/**
 * Una pantalla que ya no existe en el servidor: se recarga UNA vez.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL CASO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Softfi se parte en un trozo de JavaScript por pantalla, y cada versión los
 * nombra distinto. Con Softfi abierto en dos pestañas, la primera en
 * actualizar activa la versión nueva y los trozos viejos se borran de la copia
 * guardada; en el servidor tampoco están. La segunda pestaña sigue con el
 * código viejo, y al entrar a una pantalla que aún no había abierto pedía un
 * trozo que ya no existe: salía un error técnico en inglés en vez de la
 * pantalla.
 *
 * Recargar lo arregla —trae el código nuevo entero—, y en ese momento no se
 * pierde nada: la persona acaba de tocar un enlace, no está a mitad de cobrar
 * (y el carrito se guarda en el aparato igual).
 *
 * ⚠️ UNA vez. Si al recargar el trozo sigue sin llegar (el servidor está
 * caído, no hay señal), recargar otra vez sería un bucle: la pestaña
 * parpadeando para siempre. La segunda vez se deja que salga el error.
 */

const CLAVE = "softfi-recarga-por-version";

/** Si ya se recargó por esto hace menos de un minuto, no se vuelve a hacer. */
const VENTANA = 60_000;

/**
 * Recarga si no se hizo ya hace poco, y dice si lo hizo.
 *
 * `recargar` y `ahora` entran como parámetros solo para poder probarlo.
 */
export function recargarPorVersionNueva(
  recargar: () => void = () => window.location.reload(),
  ahora: number = Date.now(),
): boolean {
  let ultima = 0;
  try {
    ultima = Number(window.sessionStorage.getItem(CLAVE)) || 0;
  } catch {
    // Sin almacenamiento (modo privado estricto): sin memoria, sin bucle
    // posible que vigilar… pero tampoco forma de evitarlo. Mejor no recargar.
    return false;
  }
  if (ahora - ultima < VENTANA) return false;
  try {
    window.sessionStorage.setItem(CLAVE, String(ahora));
  } catch {
    return false;
  }
  recargar();
  return true;
}

/** ¿Es el error de un trozo de JavaScript que no se pudo bajar? */
export function esTrozoQueNoLlego(error: unknown): boolean {
  const texto = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /dynamically imported module|Importing a module script failed|error loading dynamically|ChunkLoadError|Loading chunk/i.test(
    texto,
  );
}
